import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";

// WHAT ROOT SEES WHEN THEY CLICK "LIVE".
//
// Every row must pass before the switch is allowed. Not a warning — a
// gate, and one that stands every time rather than only on the first
// launch. The scenario it exists for: two admins who do not talk to each
// other, one deletes something required, the other sets the site Live
// again without knowing.
//
// The server enforces it. This is so an operator can SEE what is
// outstanding rather than being refused and left guessing.
//
// TWO KINDS OF ROW, deliberately distinct:
//
//   CHECKED   the site worked it out. Nobody can tick these into
//             passing; fix the thing and they change.
//   CONFIRMED nothing here can see it — whether the checkout page has
//             been branded, whether a partner's own website works. Root
//             ticks them, and the tick records who and when.
//
// Showing only the machine-checkable half would quietly imply the rest
// does not matter, and the confirmed half contains the items most likely
// to bite precisely because they are invisible from here.
// THE SAME CHECKLIST, WITHOUT THE MODAL AROUND IT.
//
// The Going Live tab reads it; root's Live button acts on it. One source
// so the two can never disagree about what is outstanding — a tab that
// said "ready" while the button refused would be worse than either.
export function GoLiveChecklist(props) {
  return <GoLiveModal {...props} embedded />;
}

export default function GoLiveModal({ wsRequest, token, onClose, onWentLive, embedded }) {
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alsoPayments, setAlsoPayments] = useState(false);
  const [error, setError] = useState("");

  // A BLOCK BODY, RETURNING NOTHING — not an implicit return.
  //
  // React calls whatever an effect returns as its CLEANUP function. An
  // arrow with an implicit return hands it a promise, and the next time
  // the effect re-runs or the component unmounts, React tries to call
  // that promise: "r is not a function", and the whole admin panel
  // white-screens.
  //
  // Every other load() in this codebase is written with braces for the
  // same reason. Mine was not, and took down /admin/finance the moment
  // it rendered.
  const load = () => {
    wsRequest("admin.liveReadiness.get", {}, token)
      .then(setReadiness)
      .catch((e) => setError(e?.message || "Could not load the checklist."));
  };

  useEffect(load, [token]);

  const confirm = async (key, confirmed) => {
    setBusy(true);
    try {
      setReadiness(await wsRequest("admin.liveReadiness.confirm", { key, confirmed }, token));
    } catch (e) {
      showToast(e?.message || "Could not save.", "error");
    }
    setBusy(false);
  };

  const goLive = async () => {
    setBusy(true);
    try {
      // The new mode status is handed back so the banner updates without
      // a reload — the same thing the banner's own switch does with it.
      const status = await wsRequest("admin.mode.setPreferred", { mode: "live" }, token);
      // PAYMENTS SECOND, and only if asked. Live MODE and live PAYMENTS
      // are different switches — somebody could pass every check, go
      // Live, and still be taking test payments.
      if (alsoPayments) {
        await wsRequest("admin.siteSettings.update", { paymentMode: "live" }, token);
      }
      showToast(
        alsoPayments ? "The site is live and taking real payments." : "The site is live.",
        "success"
      );
      onWentLive?.(status);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Could not go live.");
      load();
    }
    setBusy(false);
  };

  if (error && !readiness) {
    return (
      <Shell onClose={onClose} embedded={embedded}>
        <p style={{ color: "var(--accent-red)" }}>{error}</p>
      </Shell>
    );
  }
  if (!readiness) {
    return (
      <Shell onClose={onClose} embedded={embedded}>
        <p>Checking…</p>
      </Shell>
    );
  }

  const { checks, confirmations, ready, outstanding, payments } = readiness;

  return (
    <Shell onClose={onClose} embedded={embedded}>
      <h3 style={{ marginTop: 0 }}>Going live</h3>

      {ready ? (
        <p style={{ color: "var(--accent-green, #5ed46a)" }}>
          Everything is in place. The site can go live.
        </p>
      ) : (
        <p style={{ opacity: 0.85 }}>
          <strong>
            {outstanding.length} item{outstanding.length === 1 ? "" : "s"} outstanding.
          </strong>{" "}
          The site cannot go live until each one is dealt with.
        </p>
      )}

      <h4 style={{ marginBottom: 6 }}>Checked</h4>
      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>
        Worked out from what this site has actually recorded. Fix the thing and the row
        changes.
      </p>
      {checks.map((c) => (
        <Row key={c.key} passed={c.passed}>
          <div>{c.label}</div>
          {c.detail && <div style={{ opacity: 0.7, fontSize: 12.5 }}>{c.detail}</div>}
          {!c.passed && c.where && (
            <div style={{ opacity: 0.85, fontSize: 12.5, marginTop: 2 }}>
              Fix it in <strong>{c.where}</strong>
            </div>
          )}
        </Row>
      ))}

      <h4 style={{ marginBottom: 6, marginTop: 18 }}>Confirmed by you</h4>
      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>
        Nothing here can check these — they are about your payment provider's settings,
        your mailboxes, and other people's websites.
      </p>
      {confirmations.map((c) => (
        <Row key={c.key} passed={c.passed}>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={c.passed}
              disabled={busy}
              onChange={(e) => confirm(c.key, e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span>{c.label}</span>
              {c.detail && (
                <span style={{ display: "block", opacity: 0.7, fontSize: 12.5 }}>{c.detail}</span>
              )}
              {/* THE VALUES TRAVEL WITH THE ROW so the tick means
                  something. A bare "reviewed ☐" gets ticked without
                  reading. */}
              {c.values && (
                <span
                  style={{
                    display: "block",
                    marginTop: 4,
                    fontSize: 12.5,
                    fontFamily: "monospace",
                    opacity: 0.9,
                  }}
                >
                  {c.values.join(" · ")}
                </span>
              )}
              {c.passed && c.confirmedBy && (
                <span style={{ display: "block", opacity: 0.6, fontSize: 12 }}>
                  {c.confirmedBy} · {new Date(c.confirmedAt).toLocaleDateString()}
                </span>
              )}
            </span>
          </label>
        </Row>
      ))}

      {/* THE SECOND SWITCH. Live mode and live payments are different
          things, and this is the moment to say so. */}
      {!embedded && (
      <div
        style={{
          marginTop: 18,
          padding: "10px 12px",
          border: "1px solid var(--border-color)",
          borderRadius: 6,
        }}
      >
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <input
            type="checkbox"
            checked={alsoPayments}
            disabled={busy || !payments.canSwitch?.ok || payments.mode === "live"}
            onChange={(e) => setAlsoPayments(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Also switch payments from Test to Live</strong>
            {payments.mode === "live" ? (
              <span style={{ display: "block", opacity: 0.7, fontSize: 12.5 }}>
                Already live.
              </span>
            ) : !payments.canSwitch?.ok ? (
              <span style={{ display: "block", color: "var(--accent-red)", fontSize: 12.5 }}>
                Not possible yet: {(payments.canSwitch?.problems || []).join(" ")}
              </span>
            ) : (
              <span style={{ display: "block", opacity: 0.75, fontSize: 12.5 }}>
                <strong>Real cards will be charged</strong>, and who switched them on is
                recorded. You can switch live payments back off at any time — stopping
                them is deliberately never blocked by a check. Demo competitions keep
                working either way.
              </span>
            )}
          </span>
        </label>
      </div>
      )}

      {error && (
        <p style={{ color: "var(--accent-red)", marginTop: 12 }}>{error}</p>
      )}

      {/* THE TAB READS; THE MODAL ACTS. Going live is root deciding to
          do it, not something to stumble into while looking at a list. */}
      {!embedded && (
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={goLive} disabled={busy || !ready}>
            {busy ? "Working…" : "Go live"}
          </button>
        </div>
      )}
    </Shell>
  );
}

function Row({ passed, children }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "7px 0",
        borderBottom: "1px solid var(--border-color)",
      }}
    >
      <span
        aria-hidden
        style={{
          color: passed ? "var(--accent-green, #5ed46a)" : "var(--accent-red)",
          fontWeight: 700,
          lineHeight: 1.4,
        }}
      >
        {passed ? "\u2713" : "\u2717"}
      </span>
      <div style={{ flex: 1, fontSize: 13.5 }}>{children}</div>
    </div>
  );
}

function Shell({ children, onClose, embedded }) {
  // EMBEDDED IN A TAB: no overlay, no dimming, no click-outside. The
  // same rows, read rather than acted on.
  if (embedded) {
    return <div>{children}</div>;
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 24,
        zIndex: 100,
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          border: "1px solid var(--border-color-light)",
          borderRadius: 10,
          padding: 20,
          width: "min(720px, 100%)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
